/**
 * The booked-shipment page body.
 *
 * Lays the sections out in order, and loads the three things only this page
 * needs: tracking, invoices, and the coupon total. The shipment itself,
 * documents and claims are loaded by the screen and passed in.
 */

import { useBookerNames } from "@/Hooks/useBookerNames";
import { useOnce } from "@/Hooks/useOnce";
import { useShipmentsApi } from "@/Hooks/useShipmentsApi";
import { useShipmentService } from "@/Services/ShipmentService";
import type { FreightClaim } from "@freightsimple/generated-dashboard-openapi-client/models/FreightClaim";
import type { GetCouponResponse } from "@freightsimple/generated-dashboard-openapi-client/models/GetCouponResponse";
import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import type { TrackingInfo } from "@freightsimple/generated-dashboard-openapi-client/models/TrackingInfo";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { useState } from "react";
import type { DocumentsSectionItem } from "@/Shared/Shipment/CustomComponents/ShipmentDocumentsSectionGeneric";
import { ViewShipmentsPageCard } from "../CustomComponents/ViewShipmentsPageCard";
import { ViewShipmentsFreightClaimsSection } from "../CustomComponents/ViewShipmentsFreightClaimsSection";
import type { ImageSectionItem } from "../CustomComponents/ViewShipmentsImageSection";
import ViewShipmentsImageSection from "../CustomComponents/ViewShipmentsImageSection";
import { ViewShipmentsAdditionalOptionsForShipment } from "./viewShipmentsAdditionalOptions";
import { ViewShipmentsBillingSectionForShipment } from "./viewShipmentsBillingSection";
import { ViewShipmentsDetailsSectionForShipment } from "./viewShipmentsDetailsSection";
import {
  ViewShipmentsDocumentsSectionForShipment,
  ViewShipmentsFailedPaymentsBanner,
  ViewShipmentsNextStepsForDeliveryForShipment,
  ViewShipmentsNextStepsForShipment,
  ViewShipmentsTrackingSectionForShipment,
  viewShipmentsCustomsDocsSectionFor,
  viewShipmentsJumpBarFor,
  viewShipmentsMapFor,
  viewShipmentsTopBarFor,
} from "./viewShipmentsSections";

interface ViewShipmentsAfterBookingProps {
  shipment: Shipment;
  freightClaims: FreightClaim[];
  onReloadShipment: () => Promise<void>;
  documentItems: Array<DocumentsSectionItem>;
  imageItems: Array<ImageSectionItem>;
}

export function ViewShipmentsAfterBooking(
  props: ViewShipmentsAfterBookingProps,
) {
  const shipment = props.shipment;
  const shipmentState = shipment.shipmentState;
  const { bookerNameFor } = useBookerNames();
  const bookedByName = bookerNameFor(shipment.bookedBy);
  const [trackingInfo, setTrackingInfo] = useState<TrackingInfo | undefined>();
  const shipmentsApi = useShipmentsApi();
  const { shipmentId } = shipment;
  const { getInvoices } = useShipmentService();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [couponsData, setCouponsData] = useState<GetCouponResponse>();

  useOnce(async function () {
    const _invoices = await getInvoices(shipment.shipmentId);
    setInvoices(_invoices);

    // Fetch coupon data
    try {
      const response = await shipmentsApi.getShipmentCouponAmount({
        shipmentId: shipment.shipmentId,
      });
      setCouponsData(response);
    } catch (error) {
      console.error("Error fetching shipment coupons:", error);
      setCouponsData(undefined);
    }
  });

  useOnce(async () => {
    loadTracking();
  });

  async function loadTracking() {
    const trackingInfo = await shipmentsApi.getTracking({
      shipmentId,
    });

    setTrackingInfo(trackingInfo);
  }

  function showCustomsDocsSection() {
    return (
      shipment.needsCustomsDocs &&
      (shipment.shipmentState === ShipmentState.BookingConfirmed ||
        shipment.shipmentState === ShipmentState.OnHold ||
        shipment.shipmentState === ShipmentState.InTransit)
    );
  }

  return (
    <>
      {viewShipmentsMapFor(shipment)}
      <ViewShipmentsPageCard>
        {/* The header cluster sits on a wider rhythm than the sections below,
            which carry their own spacing — hence the nesting rather than one
            uniform gap. Stack's default alignment lets the bars span. */}
        <Stack gap={4}>
          <Stack gap={8}>
            {viewShipmentsTopBarFor(shipment)}
            <ViewShipmentsFailedPaymentsBanner invoices={invoices} />
            {viewShipmentsJumpBarFor(shipment, bookedByName)}
          </Stack>
          <Stack gap={0}>
            {showCustomsDocsSection() &&
              viewShipmentsCustomsDocsSectionFor(
                shipment,
                props.onReloadShipment,
              )}
            {!showCustomsDocsSection() && (
              <>
                <ViewShipmentsNextStepsForShipment shipment={shipment} />
                <ViewShipmentsNextStepsForDeliveryForShipment
                  shipment={shipment}
                />
              </>
            )}
            <ViewShipmentsFreightClaimsSection
              freightClaims={props.freightClaims}
              documents={props.documentItems}
            />
            <ViewShipmentsDetailsSectionForShipment
              shipment={shipment}
              trackingInfo={trackingInfo}
              onReload={props.onReloadShipment}
            />
            {(shipmentState === ShipmentState.BookingConfirmed ||
              shipmentState === ShipmentState.InTransit) && (
              <ViewShipmentsTrackingSectionForShipment
                shipment={shipment}
                trackingInfo={trackingInfo}
              />
            )}
            <ViewShipmentsDocumentsSectionForShipment
              shipment={shipment}
              documentItems={props.documentItems}
              onReload={props.onReloadShipment}
            />
            {props.imageItems.length > 0 && (
              <ViewShipmentsImageSection
                items={props.imageItems}
                shipmentId={shipment.shipmentId}
                onReload={props.onReloadShipment}
              />
            )}
            <ViewShipmentsBillingSectionForShipment
              shipment={shipment}
              invoices={invoices}
              totalCouponAmount={couponsData?.amount || 0}
            />
            {shipmentState === ShipmentState.Delivered && (
              <ViewShipmentsTrackingSectionForShipment
                shipment={shipment}
                trackingInfo={trackingInfo}
              />
            )}
            <ViewShipmentsAdditionalOptionsForShipment
              shipment={shipment}
              onReloadShipment={props.onReloadShipment}
            />
          </Stack>
        </Stack>
      </ViewShipmentsPageCard>
    </>
  );
}
